/**
 * localStorage-backed stale-while-revalidate cache for search results.
 *
 * Strategy:
 *  - FRESH window (default 5 min): cache hit is served and NO background
 *    revalidation runs — repeated searches feel instant and we don't burn
 *    edge invocations.
 *  - STALE window (default 24 h): cache hit is painted instantly AND a
 *    background fetch revalidates the entry.
 *  - EXPIRED (> stale): treated as a miss.
 *
 * Accuracy guards:
 *  - Key includes a tuning fingerprint so changing ranking weights busts cache.
 *  - CACHE_VERSION bumps wipe everything when the result shape changes.
 *  - `invalidateSearchCache(query?)` for explicit busts (e.g. admin re-rank,
 *    feedback that should change ordering, or a manual "refresh" gesture).
 *  - Cross-tab `storage` events keep tabs in sync after invalidation.
 *  - Optional `etag` field lets callers skip writes when the server says
 *    nothing changed.
 */

const CACHE_VERSION = "v2";
const STORAGE_KEY = `atp:search-cache:${CACHE_VERSION}`;
const MAX_ENTRIES = 30;
const MAX_ENTRY_BYTES = 250_000;

const FRESH_MS = 1000 * 60 * 5;        // 5 min — serve without revalidating
const STALE_MS = 1000 * 60 * 60 * 24;  // 24 h — serve + revalidate
// > STALE_MS → treat as miss.

type CacheEntry<T> = {
  ts: number;
  data: T;
  fingerprint?: string;
  etag?: string;
};
type CacheMap<T> = Record<string, CacheEntry<T>>;

export type CacheFreshness = "fresh" | "stale" | "miss";

export type CacheHit<T> = {
  data: T;
  freshness: "fresh" | "stale";
  ageMs: number;
  etag?: string;
};

function normaliseKey(query: string, fingerprint?: string): string {
  const base = query.trim().toLowerCase().replace(/\s+/g, " ");
  return fingerprint ? `${base}::${fingerprint}` : base;
}

function readMap<T>(): CacheMap<T> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeMap<T>(map: CacheMap<T>) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Quota / disabled — drop silently.
  }
}

/**
 * Look up a cached payload. Returns freshness so the caller can decide whether
 * to revalidate in the background.
 */
export function getCachedSearch<T = unknown>(
  query: string,
  fingerprint?: string,
): CacheHit<T> | null {
  const key = normaliseKey(query, fingerprint);
  if (!key) return null;
  const map = readMap<T>();
  const entry = map[key];
  if (!entry) return null;
  const age = Date.now() - entry.ts;
  if (age > STALE_MS) return null;
  return {
    data: entry.data,
    freshness: age <= FRESH_MS ? "fresh" : "stale",
    ageMs: age,
    etag: entry.etag,
  };
}

export function setCachedSearch<T = unknown>(
  query: string,
  data: T,
  opts: { fingerprint?: string; etag?: string } = {},
) {
  const key = normaliseKey(query, opts.fingerprint);
  if (!key) return;
  let serialized: string;
  try {
    serialized = JSON.stringify(data);
  } catch {
    return;
  }
  if (serialized.length > MAX_ENTRY_BYTES) return;

  const map = readMap<T>();
  map[key] = { ts: Date.now(), data, fingerprint: opts.fingerprint, etag: opts.etag };

  // LRU trim: drop oldest until under cap.
  const keys = Object.keys(map);
  if (keys.length > MAX_ENTRIES) {
    const sorted = keys.sort((a, b) => map[a].ts - map[b].ts);
    const toDrop = sorted.slice(0, keys.length - MAX_ENTRIES);
    for (const k of toDrop) delete map[k];
  }
  writeMap(map);
}

/**
 * Invalidate a single query (all fingerprints), or wipe the entire cache when
 * `query` is omitted. Use after actions that should change rankings: feedback,
 * tuning changes, admin re-ingest, etc.
 */
export function invalidateSearchCache(query?: string) {
  if (typeof window === "undefined") return;
  if (!query) {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    return;
  }
  const base = query.trim().toLowerCase().replace(/\s+/g, " ");
  if (!base) return;
  const map = readMap();
  let changed = false;
  for (const k of Object.keys(map)) {
    if (k === base || k.startsWith(`${base}::`)) {
      delete map[k];
      changed = true;
    }
  }
  if (changed) writeMap(map);
}

/** Subscribe to cross-tab invalidations. Returns an unsubscribe fn. */
export function onSearchCacheChange(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) cb();
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}

/** Stable short fingerprint for a tuning object (or anything JSON-serialisable). */
export function fingerprintTuning(tuning: unknown): string {
  try {
    const s = JSON.stringify(tuning ?? {});
    // djb2 — tiny, no deps, stable across reloads.
    let h = 5381;
    for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    return (h >>> 0).toString(36);
  } catch {
    return "0";
  }
}
